//#region ENUM IconColourEnum
/**
 * ### IconColourEnum
 * Enum representing various icon colors and their stroke variants.
 *
 * This enum is accompanied by:
 * - `IconColourEnumHumanReadable` — human-friendly labels.
 * - `IconColourType` — a union type of all enum keys.
 *
 * @enum IconColourEnum
 * @property green - Solid green color.
 * @property green_stroke - Green stroke (outline).
 * @property red - Solid red color.
 * @property red_stroke - Red stroke (outline).
 * @property yellow - Solid yellow color.
 * @property yellow_stroke - Yellow stroke (outline).
 * @property white - Solid white color.
 * @property white_stroke - White stroke (outline).
 * @property primary - Primary theme color.
 * @property primary_stroke - Primary stroke (outline).
 * @property secondary - Secondary theme color.
 * @property secondary_stroke - Secondary stroke (outline).
 * @property black - Solid black color.
 * @property black_stroke - Black stroke (outline).
 * @property constant_white - Non-themed white color.
 * @property constant_black - Non-themed black color.
 */
export enum IconColourEnum {
  green = 'green',
  green_stroke = 'green-stroke',
  red = 'red',
  red_stroke = 'red-stroke',
  yellow = 'yellow',
  yellow_stroke = 'yellow-stroke',
  white = 'white',
  white_stroke = 'white-stroke',
  primary = 'primary',
  primary_stroke = 'primary-stroke',
  secondary = 'secondary',
  secondary_stroke = 'secondary-stroke',
  black = 'black',
  black_stroke = 'black-stroke',
  constant_white = 'constant-white',
  constant_black = 'constant-black',
  disabled = 'disabled',
  disabled_stroke = 'disabled-stroke',
  none = 'none',
}

//#endregion

//#region ENUM IconColourEnumHumanReadable
/**
 * ### IconColourEnumHumanReadable
 * Human-readable labels for each member of `IconColourEnum`.
 *
 * @enum IconColourEnumHumanReadable
 * @property green - Label for green.
 * @property green_stroke - Label for green stroke.
 * @property red - Label for red.
 * @property red_stroke - Label for red stroke.
 * @property yellow - Label for yellow.
 * @property yellow_stroke - Label for yellow stroke.
 * @property white - Label for white.
 * @property white_stroke - Label for white stroke.
 * @property primary - Label for primary color.
 * @property primary_stroke - Label for primary stroke.
 * @property secondary - Label for secondary color.
 * @property secondary_stroke - Label for secondary stroke.
 * @property black - Label for black.
 * @property black_stroke - Label for black stroke.
 * @property constant_white - Label for constant white.
 * @property constant_black - Label for constant black.
 */
export enum IconColourEnumHumanReadable {
  /** Label for green */
  green = 'Green',

  /** Label for green stroke */
  green_stroke = 'Green Stroke',

  /** Label for red */
  red = 'Red',

  /** Label for red stroke */
  red_stroke = 'Red Stroke',

  /** Label for yellow */
  yellow = 'Yellow',

  /** Label for yellow stroke */
  yellow_stroke = 'Yellow Stroke',

  /** Label for white */
  white = 'White',

  /** Label for white stroke */
  white_stroke = 'White Stroke',

  /** Label for primary color */
  primary = 'Primary',

  /** Label for primary stroke */
  primary_stroke = 'Primary Stroke',

  /** Label for secondary color */
  secondary = 'Secondary',

  /** Label for secondary stroke */
  secondary_stroke = 'Secondary Stroke',

  /** Label for black */
  black = 'Black',

  /** Label for black stroke */
  black_stroke = 'Black Stroke',

  /** Label for constant white */
  constant_white = 'Constant White',

  /** Label for constant black */
  constant_black = 'Constant Black',
}

//#endregion

//#region TYPE IconColourType
/**
 * ### IconColourType
 * Union type of all keys in `IconColourEnum`.
 *
 * @type IconColourType
 * @example
 * // Possible values: 'green' | 'green-stroke' | 'red' | 'red-stroke' | 'yellow' | ...
 */
export type IconColourType = keyof typeof IconColourEnum;
//#endregion
